import { useEffect, useState } from "react";
import { Activity, Users, LogOut, AlertTriangle } from "lucide-react";
import AdminLayout from "../../layouts/AdminLayout";
import { apiRequest } from "../../utils/apiRequest";
import FullPageSpinner from "../../components/FullPageSpinner";

const formatClockTime = (dateString) => {
  if (!dateString) return "—";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";

  const now = new Date();

  // strip time for accurate day comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  const diffDays = Math.floor(
    (today.getTime() - targetDay.getTime()) / 86400000,
  );

  const timePart = new Intl.DateTimeFormat("en-GB", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);

  if (diffDays === 0) {
    return `Today, ${timePart}`;
  }

  if (diffDays === 1) {
    return `Yesterday, ${timePart}`;
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

const formatActivityTime = (dateString) => {
  if (!dateString) return "—";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  if (diffHours < 24) return `${diffHours} hr ago`;
  if (diffDays === 1) return "Yesterday";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

export default function AdminDashboard() {
  const token = localStorage.getItem("staff_portal_token");

  const [summary, setSummary] = useState({
    total_staff: 0,
    clocked_in: 0,
    hours_this_week: "0h 00m",
    expiring_docs: 0,
  });
  const [clockedInStaff, setClockedInStaff] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [clockingOut, setClockingOut] = useState(false);
  const [clockOutError, setClockOutError] = useState("");

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);

      const [summaryRes, activityRes] = await Promise.all([
        apiRequest({
          script: "admin-dashboard-summary",
          token,
          data: {},
        }),
        apiRequest({
          script: "admin-get-recent-activity",
          token,
          data: {
            limit: 10,
          },
        }),
      ]);

      if (summaryRes?.success) {
        setSummary(summaryRes.summary || {});
        setClockedInStaff(summaryRes.clocked_in_staff || []);
      } else {
        setFetchError(summaryRes?.error || "Failed to load dashboard summary");
      }

      if (activityRes?.success) {
        setRecentActivity(activityRes.activities || []);
      }

      setLoading(false);
    };

    fetchDashboardData();
  }, [token]);

  const handleRefresh = () => {
    setFetchError("");
    // Trigger the useEffect by updating a dummy state or just calling the logic again
    // For simplicity, I'll just re-trigger the fetch logic if it's extracted, 
    // but here I can just use a state to force re-run.
    window.location.reload(); 
  };

  const handleClockOut = async () => {
    if (!selectedStaff) return;

    setClockingOut(true);
    setClockOutError("");

    const res = await apiRequest({
      script: "admin-clock-out-staff",
      token,
      data: {
        target_staff_user_id: selectedStaff.staff_user_id,
      },
    });

    setClockingOut(false);

    if (res?.success) {
      setSelectedStaff(null);
      // Refresh data
      const fetchDashboardData = async () => {
        setLoading(true);
        const [summaryRes, activityRes] = await Promise.all([
          apiRequest({
            script: "admin-dashboard-summary",
            token,
            data: {},
          }),
          apiRequest({
            script: "admin-get-recent-activity",
            token,
            data: { limit: 10 },
          }),
        ]);

        if (summaryRes?.success) {
          setSummary(summaryRes.summary || {});
          setClockedInStaff(summaryRes.clocked_in_staff || []);
        }

        if (activityRes?.success) {
          setRecentActivity(activityRes.activities || []);
        }
        setLoading(false);
      };
      fetchDashboardData();
    } else {
      setClockOutError(res?.error || "Failed to clock out staff member");
    }
  };

  return (
    <AdminLayout title="Dashboard">
      <FullPageSpinner show={loading} />

      <div className="space-y-6">
        {fetchError && (
          <div className="flex items-center justify-between rounded-2xl bg-red-50 p-4 text-sm font-medium text-red-600 ring-1 ring-red-100">
            <div className="flex items-center gap-3">
              <AlertTriangle size={20} />
              {fetchError}
            </div>
            <button 
              onClick={handleRefresh}
              className="rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 to-blue-50 p-6 shadow-sm">
            <p className="text-sm font-medium text-sky-600">Total Staff</p>
            <h3 className="mt-2 text-3xl font-bold text-sky-900">
              {summary.total_staff}
            </h3>
          </div>

          <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50 p-6 shadow-sm">
            <p className="text-sm font-medium text-emerald-600">
              Currently Clocked In
            </p>
            <h3 className="mt-2 text-3xl font-bold text-emerald-900">
              {summary.clocked_in}
            </h3>
          </div>

          <div className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50 p-6 shadow-sm">
            <p className="text-sm font-medium text-violet-600">
              Hours This Week
            </p>
            <h3 className="mt-2 text-3xl font-bold text-violet-900">
              {summary.hours_this_week}
            </h3>
          </div>

          <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-6 shadow-sm">
            <p className="text-sm font-medium text-amber-600">Expiring Docs</p>
            <h3 className="mt-2 text-3xl font-bold text-amber-900">
              {summary.expiring_docs}
            </h3>
          </div>
        </div>

        <div id="live-staff-section" className="grid grid-cols-1 gap-6 xl:grid-cols-[1.3fr_0.9fr]">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-slate-50 to-emerald-50 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Live Clocked-In Staff
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Staff currently on shift
                </p>
              </div>

              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 ring-1 ring-emerald-200">
                <Users size={20} className="text-emerald-700" />
              </div>
            </div>

            {clockedInStaff.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-slate-500">
                No staff are currently clocked in.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {clockedInStaff.map((staff, index) => (
                  <div
                    key={staff.attendance_id}
                    className={`flex flex-col gap-4 px-6 py-5 md:flex-row md:items-center md:justify-between ${
                      index % 2 === 0 ? "bg-white" : "bg-slate-50/60"
                    }`}
                  >
                    <div className="min-w-0">
                      {/* mobile inline (below 768px) */}
                      <div className="flex items-center gap-2 md:hidden">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {staff.staff_name}
                        </p>
                        <span className="shrink-0 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                          {staff.staff_id}
                        </span>
                      </div>

                      {/* tablet & desktop stacked (768px and above) */}
                      <div className="hidden md:block">
                        <p className="text-sm font-semibold text-slate-900">
                          {staff.staff_name}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {staff.staff_id}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col gap-4 md:flex-row md:items-center">
                      <div className="grid grid-cols-2 gap-3 md:w-[280px]">
                        <div className="rounded-xl bg-emerald-50 px-3 py-2 ring-1 ring-emerald-100">
                          <p className="text-[10px] font-medium text-emerald-600">
                            In
                          </p>
                          <p className="text-xs font-semibold text-emerald-900">
                            {formatClockTime(staff.clock_in)}
                          </p>
                        </div>

                        <div className="rounded-xl bg-sky-50 px-3 py-2 ring-1 ring-sky-100">
                          <p className="text-[10px] font-medium text-sky-600">
                            Worked
                          </p>
                          <p className="text-xs font-semibold text-sky-900">
                            {staff.worked_display}
                          </p>
                        </div>
                      </div>

                      <button
                        onClick={() => setSelectedStaff(staff)}
                        className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-600 transition-colors hover:bg-red-600 hover:text-white"
                        title="Clock Out"
                      >
                        <LogOut size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-slate-50 to-violet-50 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Recent Activity
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Latest actions across the system
                </p>
              </div>

              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-100 ring-1 ring-violet-200">
                <Activity size={20} className="text-violet-700" />
              </div>
            </div>

            {recentActivity.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-slate-500">
                No recent activity yet.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {recentActivity.map((item, index) => (
                  <div
                    key={item.id}
                    className={`px-6 py-4 ${
                      index % 2 === 0 ? "bg-white" : "bg-slate-50/60"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900">
                          {item.description}
                        </p>
                      </div>

                      <p className="whitespace-nowrap text-xs text-slate-500">
                        {formatActivityTime(item.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Simple Confirmation Modal */}
      {selectedStaff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm animate-in fade-in zoom-in duration-200">
            <div className="overflow-hidden rounded-2xl bg-white p-6 shadow-2xl">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-100 text-red-600 mb-4">
                <LogOut size={24} />
              </div>
              
              <h3 className="text-lg font-bold text-slate-900">Confirm Clock Out</h3>
              <p className="mt-2 text-sm text-slate-500">
                Are you sure you want to clock out <span className="font-semibold text-slate-900">{selectedStaff.staff_name}</span>? 
              </p>

              {clockOutError && (
                <div className="mt-4 flex items-center gap-2 rounded-xl bg-red-50 p-3 text-xs font-medium text-red-600 ring-1 ring-red-100">
                  <AlertTriangle size={14} />
                  {clockOutError}
                </div>
              )}

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => {
                    setSelectedStaff(null);
                    setClockOutError("");
                  }}
                  disabled={clockingOut}
                  className="flex-1 rounded-xl bg-slate-100 py-3 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClockOut}
                  disabled={clockingOut}
                  className="flex-1 rounded-xl bg-red-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {clockingOut ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  ) : (
                    "Confirm"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
